/**
 * Accès à l'API publique SwiftDeliver — offre DISTINCTE du SaaS (migration 0048). Deux drapeaux, posés
 * UNIQUEMENT par un administrateur de la plateforme, journalisés :
 *   - `api_sandbox`    → clés de test `sk_test_` et documentation ;
 *   - `api_production` → clés live `sk_live_`.
 * Deux cas :
 *   (b) un transporteur avec sa flotte = une ORGANISATION SaaS : les drapeaux sont ceux de l'organisation ;
 *   (a) un client API sans flotte = un CLIENT OPÉRÉ (`client_accounts`, type PME) de l'organisation opératrice :
 *       les drapeaux sont ceux du client, son compte de connexion est le profil relié (`profile_id`).
 * L'organisation opératrice a les deux droits d'office (sa RPC refuse avec `organisation_operatrice`).
 * Logique pure + enveloppes typées des RPC `platform_*_api*` et de l'edge function `creer-client-api` ; les appels
 * prennent la fonction `rpc` / `invoke` en argument (jamais le client importé ici) pour que les tests vérifient
 * la forme exacte des arguments.
 */
import { ERREURS_CONTACT, afficherTelephone, corpsContact, resumeAcces, validerContact } from './commun'

/** Ce que les enveloppes demandent à un client Supabase : `supabase.rpc(nom, args)`. */
export type AppelRpc = (
  nom: string,
  args?: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>

export type ModeCle = 'test' | 'live'
export type ActionJournalApi = 'acces' | 'revocation_cle' | 'reprise' | 'compte_client'

/** Une ligne de `platform_acces_api()`. */
export interface LigneAccesApi {
  org_id: string
  name: string
  is_operator: boolean
  api_sandbox: boolean
  api_production: boolean
  cles_test_actives: number
  cles_live_actives: number
  derniere_utilisation: string | null
  dernier_changement: string | null
}

/** Une ligne de `platform_cles_api(p_org, p_client)` — jamais l'empreinte de la clé. */
export interface CleApi {
  id: string
  label: string
  prefix: string
  mode: ModeCle
  client_account_id: string | null
  scopes: string[]
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

/** Une ligne de `platform_journal_acces_api(p_org, p_client)`, la plus récente d'abord. */
export interface EntreeJournalApi {
  id: string
  created_at: string
  action: ActionJournalApi
  old_sandbox: boolean | null
  new_sandbox: boolean | null
  old_production: boolean | null
  new_production: boolean | null
  note: string
  /** Nom (ou e-mail) de l'auteur ; null pour la reprise automatique de la migration. */
  auteur: string | null
}

export interface ChangementAccesApi {
  org: string
  sandbox: boolean
  production: boolean
  note: string
}

/* ───────────── Lecture des lignes ───────────── */

const txt = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
const entier = (v: unknown): number => {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0
}
const boolOuNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
const lignes = (d: unknown): Record<string, unknown>[] => (Array.isArray(d) ? (d as Record<string, unknown>[]) : [])

export function ligneAccesApi(r: Record<string, unknown>): LigneAccesApi {
  return {
    org_id: String(r['org_id']),
    name: String(r['name'] ?? ''),
    is_operator: r['is_operator'] === true,
    api_sandbox: r['api_sandbox'] === true,
    api_production: r['api_production'] === true,
    cles_test_actives: entier(r['cles_test_actives']),
    cles_live_actives: entier(r['cles_live_actives']),
    derniere_utilisation: txt(r['derniere_utilisation']),
    dernier_changement: txt(r['dernier_changement']),
  }
}

export function cleApi(r: Record<string, unknown>): CleApi {
  return {
    id: String(r['id']),
    label: String(r['label'] ?? ''),
    prefix: String(r['prefix'] ?? ''),
    mode: r['mode'] === 'live' ? 'live' : 'test',
    client_account_id: txt(r['client_account_id']),
    scopes: Array.isArray(r['scopes']) ? r['scopes'].filter((s): s is string => typeof s === 'string') : [],
    created_at: String(r['created_at'] ?? ''),
    last_used_at: txt(r['last_used_at']),
    revoked_at: txt(r['revoked_at']),
  }
}

export function entreeJournalApi(r: Record<string, unknown>): EntreeJournalApi {
  const a = r['action']
  const action: ActionJournalApi = a === 'revocation_cle' || a === 'reprise' || a === 'compte_client' ? a : 'acces'
  return {
    id: String(r['id']),
    created_at: String(r['created_at'] ?? ''),
    action,
    old_sandbox: boolOuNull(r['old_sandbox']),
    new_sandbox: boolOuNull(r['new_sandbox']),
    old_production: boolOuNull(r['old_production']),
    new_production: boolOuNull(r['new_production']),
    note: String(r['note'] ?? ''),
    auteur: txt(r['auteur_nom']) ?? txt(r['auteur_email']),
  }
}

/* ───────────── Libellés ───────────── */

/** « Test + Production », « Test », « Production », « Aucun » — pour une organisation comme pour un client API. */
export function libelleDrapeauxApi(l: Pick<LigneAccesApi, 'api_sandbox' | 'api_production'>): string {
  if (l.api_sandbox && l.api_production) return 'Test + Production'
  if (l.api_sandbox) return 'Test'
  if (l.api_production) return 'Production'
  return 'Aucun'
}

/** Idem, avec « D'office (opératrice) » pour l'organisation opératrice. */
export function libelleAccesApi(l: Pick<LigneAccesApi, 'is_operator' | 'api_sandbox' | 'api_production'>): string {
  return l.is_operator ? "D'office (opératrice)" : libelleDrapeauxApi(l)
}

/** Couleur du badge d'accès : production en vert, test seul en bleu, rien en gris. */
export function classeAccesApi(l: Pick<LigneAccesApi, 'api_sandbox' | 'api_production'>): 'vert' | 'info' | 'gris' {
  return l.api_production ? 'vert' : l.api_sandbox ? 'info' : 'gris'
}

export const LIBELLE_MODE_CLE: Record<ModeCle, string> = { test: 'Test', live: 'Production' }

/** « sk_live_ab12… » — le préfixe stocké, complété de son motif s'il ne le porte pas déjà. */
export function prefixeCle(c: Pick<CleApi, 'prefix' | 'mode'>): string {
  const base = c.prefix.startsWith('sk_') ? c.prefix : `sk_${c.mode}_${c.prefix}`
  return `${base}…`
}

export const cleActive = (c: Pick<CleApi, 'revoked_at'>): boolean => c.revoked_at === null

const ouiNon = (v: boolean | null): string => (v === null ? '—' : v ? 'oui' : 'non')

/** Ce que dit une ligne du journal : les deux drapeaux avant → après, ou la nature de l'acte. */
export function decrireJournalApi(e: Pick<EntreeJournalApi, 'action' | 'old_sandbox' | 'new_sandbox' | 'old_production' | 'new_production'>): string {
  if (e.action === 'revocation_cle') return 'Clé révoquée'
  if (e.action === 'reprise') return 'Reprise automatique'
  if (e.action === 'compte_client') return 'Compte développeur créé ou rattaché'
  return `Test : ${ouiNon(e.old_sandbox)} → ${ouiNon(e.new_sandbox)} · Production : ${ouiNon(e.old_production)} → ${ouiNon(e.new_production)}`
}

/** Qui a agi : le nom de l'auteur, « Système » pour la reprise automatique, « Auteur inconnu » sinon. */
export function libelleAuteurApi(e: Pick<EntreeJournalApi, 'action' | 'auteur'>): string {
  if (e.auteur?.trim()) return e.auteur.trim()
  return e.action === 'reprise' ? 'Système' : 'Auteur inconnu'
}

/* ───────────── Note obligatoire ───────────── */

export const NOTE_ACCES_MAX = 500

/** La note qui sert de preuve : rognée, de 1 à 500 caractères. */
export function validerNoteAcces(note: string): { ok: true; note: string } | { ok: false; message: string } {
  const n = note.trim()
  if (!n) return { ok: false, message: "Indiquez la raison du changement : c'est elle qui sert de preuve." }
  if (n.length > NOTE_ACCES_MAX) return { ok: false, message: `La raison fait ${NOTE_ACCES_MAX} caractères au plus (${n.length} saisis).` }
  return { ok: true, note: n }
}

/* ───────────── Erreurs en phrases ───────────── */

const PHRASES: [string, string][] = [
  ['note_required', "Indiquez la raison du changement : elle est obligatoire."],
  ['not_platform_admin', "Votre compte n'est pas administrateur de la plateforme : le changement est refusé."],
  ['parametres_requis', 'Paramètres manquants : rechargez la page puis recommencez.'],
  ['organisation_inconnue', "Cette organisation n'existe plus. Rechargez la page."],
  ['organisation_operatrice', "L'organisation opératrice a les deux accès d'office : il n'y a rien à modifier (ses clients API se gèrent dans l'onglet Clients API)."],
  ['client_inconnu', "Ce client n'existe plus. Rechargez la page."],
  ['client_non_opere', "Seul un client de l'organisation opératrice peut recevoir un accès API de client."],
  ['cle_inconnue', "Cette clé n'existe plus. Rechargez la page."],
]

/** Traduit l'erreur d'une RPC d'accès API (le `message` PostgREST est le code) en phrase. */
export function messageAccesApi(erreur: unknown): string {
  let message = ''
  let code = ''
  if (typeof erreur === 'string') message = erreur
  else if (erreur && typeof erreur === 'object') {
    const o = erreur as { message?: unknown; code?: unknown }
    message = typeof o.message === 'string' ? o.message : ''
    code = typeof o.code === 'string' ? o.code : ''
  }
  const m = message.toLowerCase()
  for (const [c, phrase] of PHRASES) if (m.includes(c)) return phrase
  if (code === '42501') return "Votre compte n'est pas administrateur de la plateforme : le changement est refusé."
  if (code === 'PGRST202' || m.includes('could not find the function')) {
    return "La gestion de l'accès API n'est pas encore installée sur la base (migration 0048 à pousser)."
  }
  if (code === 'PGRST301' || m.includes('jwt')) return 'Session expirée — reconnectez-vous puis recommencez.'
  if (m.includes('failed to fetch') || m.includes('load failed') || m.includes('networkerror')) {
    return 'Connexion impossible : vérifiez votre réseau puis réessayez. Rien n’a été enregistré.'
  }
  return `L'opération a échoué${message ? ` (${message})` : ''}. Rien n’a été confirmé.`
}

/* ───────────── Enveloppes des RPC ───────────── */

/** Lève l'erreur de la RPC en conservant son `code` et son `message` (le code métier). */
function leverSiErreur(r: { error: { message: string; code?: string } | null }): void {
  if (r.error) throw Object.assign(new Error(r.error.message), { code: r.error.code })
}

export async function lireAccesApi(rpc: AppelRpc): Promise<LigneAccesApi[]> {
  const r = await rpc('platform_acces_api')
  leverSiErreur(r)
  return lignes(r.data).map(ligneAccesApi)
}

/** Pose les DEUX drapeaux d'un coup (celui qui ne change pas garde sa valeur). La note est rognée. */
export async function definirAccesApi(rpc: AppelRpc, m: ChangementAccesApi): Promise<void> {
  const r = await rpc('platform_definir_acces_api', {
    p_org: m.org,
    p_sandbox: m.sandbox,
    p_production: m.production,
    p_note: m.note.trim(),
  })
  leverSiErreur(r)
}

/** Clés de l'organisation (`client` null : ses clés non limitées) ou d'un client API (org = l'opératrice). */
export async function lireClesApi(rpc: AppelRpc, org: string, client: string | null = null): Promise<CleApi[]> {
  const r = await rpc('platform_cles_api', { p_org: org, p_client: client })
  leverSiErreur(r)
  return lignes(r.data).map(cleApi)
}

/** Idempotent côté base : révoquer deux fois la même clé ne lève rien. */
export async function revoquerCleApi(rpc: AppelRpc, id: string, note: string): Promise<void> {
  const r = await rpc('platform_revoquer_cle_api', { p_id: id, p_note: note.trim() })
  leverSiErreur(r)
}

/** Journal de l'organisation (`client` null) ou d'un client API, le plus récent d'abord. */
export async function lireJournalAccesApi(rpc: AppelRpc, org: string, client: string | null = null): Promise<EntreeJournalApi[]> {
  const r = await rpc('platform_journal_acces_api', { p_org: org, p_client: client })
  leverSiErreur(r)
  return lignes(r.data).slice(0, 100).map(entreeJournalApi)
}

/* ═════════════ Cas (a) : clients API sans flotte (clients opérés de l'opératrice) ═════════════ */

/** Une ligne de `platform_clients_api()` : agrégats et nom du client, aucune course. */
export interface LigneClientApi {
  client_id: string
  client_nom: string
  /** L'organisation opératrice, propriétaire du client (p_org des clés et du journal). */
  org_id: string
  /** Code de `client_onboarding_status` (prospect, documents, contrat_envoye, contrat_signe, actif, refuse, suspendu). */
  onboarding_status: string
  api_sandbox: boolean
  api_production: boolean
  /** 0 ou 1 : un compte de connexion est relié au client. */
  comptes: number
  cles_test_actives: number
  cles_live_actives: number
  derniere_utilisation: string | null
  dernier_changement: string | null
}

export function ligneClientApi(r: Record<string, unknown>): LigneClientApi {
  return {
    client_id: String(r['client_id']),
    client_nom: String(r['client_nom'] ?? ''),
    org_id: String(r['org_id'] ?? ''),
    onboarding_status: String(r['onboarding_status'] ?? ''),
    api_sandbox: r['api_sandbox'] === true,
    api_production: r['api_production'] === true,
    comptes: Math.min(1, entier(r['comptes'])),
    cles_test_actives: entier(r['cles_test_actives']),
    cles_live_actives: entier(r['cles_live_actives']),
    derniere_utilisation: txt(r['derniere_utilisation']),
    dernier_changement: txt(r['dernier_changement']),
  }
}

export async function lireClientsApi(rpc: AppelRpc): Promise<LigneClientApi[]> {
  const r = await rpc('platform_clients_api')
  leverSiErreur(r)
  return lignes(r.data).map(ligneClientApi)
}

export interface ChangementAccesClientApi {
  client: string
  sandbox: boolean
  production: boolean
  note: string
}

/** Pose les DEUX drapeaux d'un client API d'un coup (celui qui ne change pas garde sa valeur). La note est rognée. */
export async function definirAccesApiClient(rpc: AppelRpc, m: ChangementAccesClientApi): Promise<void> {
  const r = await rpc('platform_definir_acces_api_client', {
    p_client: m.client,
    p_sandbox: m.sandbox,
    p_production: m.production,
    p_note: m.note.trim(),
  })
  leverSiErreur(r)
}

/** Le compte de connexion d'un client API : une ligne de `platform_comptes_client_api`. */
export interface CompteClientApi {
  profile_id: string
  nom: string | null
  email: string | null
  phone: string | null
  created_at: string
}

export function compteClientApi(r: Record<string, unknown>): CompteClientApi {
  return {
    profile_id: String(r['profile_id']),
    nom: txt(r['nom']),
    email: txt(r['email']),
    phone: txt(r['phone']),
    created_at: String(r['created_at'] ?? ''),
  }
}

/** 0 ou 1 compte. */
export async function lireComptesClientApi(rpc: AppelRpc, client: string): Promise<CompteClientApi[]> {
  const r = await rpc('platform_comptes_client_api', { p_client: client })
  leverSiErreur(r)
  return lignes(r.data).map(compteClientApi)
}

export type ClasseStatut = 'vert' | 'info' | 'alerte' | 'rouge' | 'gris'

/** Statut d'onboarding (contrat) d'un client opéré : libellé et couleur de badge. */
const ONBOARDING: Record<string, { libelle: string; classe: ClasseStatut }> = {
  prospect: { libelle: 'Prospect', classe: 'gris' },
  documents: { libelle: 'Documents', classe: 'info' },
  contrat_envoye: { libelle: 'Contrat envoyé', classe: 'info' },
  contrat_signe: { libelle: 'Contrat signé', classe: 'info' },
  actif: { libelle: 'Actif', classe: 'vert' },
  refuse: { libelle: 'Refusé', classe: 'rouge' },
  suspendu: { libelle: 'Suspendu', classe: 'alerte' },
}

export function statutOnboardingApi(code: string): { libelle: string; classe: ClasseStatut } {
  return ONBOARDING[code] ?? { libelle: code || 'Inconnu', classe: 'gris' }
}

export const NOTE_COURSES_CLIENT_API =
  "Les courses de ce client sont livrées par notre flotte (offre opérée) ; ses courses réelles exigent l'onboarding terminé (contrat), ses courses de test non."

/** Où se connecte le développeur d'un client API, et la consigne qui suit la création de son compte. */
export const URL_PORTAIL_DEVELOPPEURS = 'https://developpeurs.yiriwa-group.com'
export const PHRASE_OUVRIR_ACCES_TEST = "Ouvrez ensuite l'accès de test dans la fiche du client API : aucune clé n'est possible avant."

/* ───────────── Clients opérés (pour rattacher un client existant) ───────────── */

/** Un client PME de l'opératrice, proposé dans « Client opéré existant ». */
export interface ClientOpere {
  id: string
  name: string
  /** Déjà relié à un compte de connexion : on ne peut pas en rattacher un second. */
  profile_id: string | null
}

export function clientOpere(r: Record<string, unknown>): ClientOpere {
  return { id: String(r['id']), name: String(r['name'] ?? ''), profile_id: txt(r['profile_id']) }
}

/* ───────────── Création d'un client API : edge function `creer-client-api` ───────────── */

/** Ce que saisit l'administrateur : un NOUVEAU client (raison sociale) ou un client opéré EXISTANT. */
export interface FormulaireClientApi {
  mode: 'nouveau' | 'existant'
  /** Raison sociale (mode « nouveau »). */
  name: string
  /** Identifiant du client opéré (mode « existant »). */
  clientId: string
  contact_name: string
  email: string
  telephone: { texte: string; iso: string }
}

/** Règles du formulaire, les mêmes que l'edge function (qui les revérifie) : null = valide. */
export function validerClientApi(f: FormulaireClientApi): string | null {
  if (f.mode === 'nouveau' && !f.name.trim()) return 'La raison sociale est obligatoire.'
  if (f.mode === 'existant' && !f.clientId) return 'Choisissez le client opéré à rattacher.'
  if (!f.contact_name.trim()) return 'Le nom du contact est obligatoire.'
  const contact = validerContact({ email: f.email, telephone: f.telephone })
  return contact.ok ? null : contact.message
}

/**
 * Corps de `creer-client-api` : `name` (nouveau) OU `client_id` (existant), `contact_name`, puis `email` et/ou
 * `phone` (E.164) + `pays`. Seules les clés présentes sont émises. N'ouvre AUCUN accès.
 */
export function corpsCreationClientApi(f: FormulaireClientApi): Record<string, unknown> {
  const refus = validerClientApi(f)
  if (refus) throw new Error(refus)
  const contact = validerContact({ email: f.email, telephone: f.telephone })
  if (!contact.ok) throw new Error(contact.message)
  return {
    ...(f.mode === 'nouveau' ? { name: f.name.trim() } : { client_id: f.clientId }),
    contact_name: f.contact_name.trim(),
    ...corpsContact(contact),
  }
}

/** Réponse de `creer-client-api`. Sans mot de passe temporaire (`''`), jamais « null » à l'écran. */
export interface ResultatClientApi {
  clientId: string
  clientCree: boolean
  email: string | null
  /** E.164 ; null si le compte est créé avec un e-mail seul. */
  phone: string | null
  tempPassword: string
  compteExistant: boolean
  connexion: 'mot_de_passe' | 'whatsapp' | 'existant'
}

export function lireReponseClientApi(rep: Record<string, unknown>, f: FormulaireClientApi): ResultatClientApi {
  const saisi = validerContact({ email: f.email, telephone: f.telephone })
  const email = txt(rep['email']) ?? (saisi.ok ? saisi.email : null)
  const phone = txt(rep['phone']) ?? (saisi.ok ? saisi.phone : null)
  const tempPassword = typeof rep['temp_password'] === 'string' ? rep['temp_password'] : ''
  const c = rep['connexion']
  const connexion: ResultatClientApi['connexion'] =
    c === 'whatsapp' || c === 'existant' || c === 'mot_de_passe' ? c : tempPassword ? 'mot_de_passe' : !email && phone ? 'whatsapp' : 'existant'
  return {
    clientId: String(rep['client_id'] ?? f.clientId),
    clientCree: rep['client_cree'] === true,
    email,
    phone,
    tempPassword,
    compteExistant: rep['compte_existant'] === true || (tempPassword === '' && connexion !== 'whatsapp'),
    connexion,
  }
}

/** Ce que l'écran de succès dit de l'identifiant et du secret (le secret n'existe que pour un compte neuf avec e-mail). */
export function resumeAccesClientApi(a: ResultatClientApi): { identifiants: string[]; secret: string | null; phrase: string } {
  const r = resumeAcces({ email: a.email, phone: a.phone, temp_password: a.tempPassword || null, connexion: a.connexion })
  const identifiants = [a.email, a.phone ? afficherTelephone(a.phone) : ''].filter((x): x is string => !!x)
  return { identifiants, secret: r.secret, phrase: r.phrase }
}

/** Le texte du bouton « Copier les accès » : espace développeur, identifiants, secret ou phrase. */
export function texteAccesClientApi(a: ResultatClientApi): string {
  const r = resumeAccesClientApi(a)
  return `Espace développeur : ${URL_PORTAIL_DEVELOPPEURS}\n`
    + r.identifiants.map((i) => `Identifiant : ${i}\n`).join('')
    + (r.secret ? `Mot de passe temporaire : ${r.secret}` : r.phrase)
}

const minuscule = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

const ERREURS_CREATION: Record<string, string> = {
  forbidden: "Votre compte n'est pas administrateur de la plateforme.",
  unauthorized: 'Session expirée — reconnectez-vous.',
  invalid_email: 'Adresse e-mail invalide.',
  operator_not_found: "Votre compte n'est rattaché à aucune organisation opératrice.",
  client_inconnu: "Ce client opéré n'existe pas (ou n'est pas un client PME de l'opératrice). Rechargez la page.",
  client_deja_relie: "Ce client est déjà relié à un autre compte : un client n'a qu'un seul compte développeur.",
  email_already_used: 'Cet e-mail a déjà un compte.',
  create_user_failed: "Le compte du développeur n'a pas pu être créé.",
  client_failed: "La fiche du client n'a pas pu être créée (rien n'a été conservé).",
  relier_failed: "Le compte n'a pas pu être relié au client (rien n'a été conservé).",
  ...ERREURS_CONTACT,
}

const CHAMPS_MANQUANTS: Record<string, string> = {
  name: 'La raison sociale est obligatoire.',
  contact_name: 'Le nom du contact est obligatoire.',
  email: 'Indiquez un e-mail ou un numéro WhatsApp.',
  client_id: 'Client invalide : rechargez la page.',
}

/** Traduit le code d'erreur de `creer-client-api` (corps de la réponse) en phrase. */
export function traduireErreurClientApi(code: string, detail = '', repli = '', champ = ''): string {
  if (code === 'invalid_phone') return `Création refusée : ${detail || minuscule(ERREURS_CONTACT['invalid_phone'] ?? 'numéro invalide.')}`
  if (code === 'missing_params') return `Création refusée : ${minuscule(CHAMPS_MANQUANTS[champ] ?? 'paramètres manquants.')}`
  const phrase = ERREURS_CREATION[code]
  return `Création refusée : ${phrase ? minuscule(phrase) : detail || code || repli || 'réessayez.'}`
}

/** Ce que l'enveloppe demande à `supabase.functions.invoke`. */
export type AppelFonction = (
  nom: string,
  options: { body: Record<string, unknown> },
) => PromiseLike<{ data: unknown; error: unknown }>

/** Le code d'erreur est dans le corps de la réponse HTTP, pas dans `error.message`. */
export async function lireCorpsErreur(error: unknown): Promise<{ code: string; detail: string; field: string; message: string }> {
  const m = error && typeof error === 'object' ? (error as { message?: unknown }).message : undefined
  const message = typeof m === 'string' ? m : ''
  let code = ''
  let detail = ''
  let field = ''
  try {
    const ctx = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context
    const j = (await ctx?.json?.()) as { error?: string; detail?: string; field?: string } | undefined
    code = j?.error ?? ''
    detail = j?.detail ?? ''
    field = j?.field ?? ''
  } catch {
    /* corps non JSON : on garde le message brut */
  }
  return { code, detail, field, message }
}

/**
 * Crée un client API (compte développeur + fiche de client opéré, ou rattachement à un client existant).
 * N'ouvre AUCUN accès : c'est `definirAccesApiClient` ensuite. Lève une `Error` au message déjà en français.
 */
export async function creerClientApi(invoke: AppelFonction, f: FormulaireClientApi): Promise<ResultatClientApi> {
  const body = corpsCreationClientApi(f)
  const { data, error } = await invoke('creer-client-api', { body })
  if (error) {
    const e = await lireCorpsErreur(error)
    throw new Error(traduireErreurClientApi(e.code, e.detail, e.message, e.field))
  }
  const rep = (data && typeof data === 'object' ? data : null) as Record<string, unknown> | null
  if (!rep || rep['ok'] !== true) {
    throw new Error(traduireErreurClientApi(String(rep?.['error'] ?? ''), String(rep?.['detail'] ?? ''), '', String(rep?.['field'] ?? '')))
  }
  return lireReponseClientApi(rep, f)
}
